<div class="wizard">

  <header class="page-top">
    <h2 class="page-title">Create Agent</h2>
  </header>

  <div class="wizard__body">
    <header class="wizard__topbar">
      <div class="wizard__heading">
        <span class="eyebrow">Agent studio</span>
        <h1 class="wizard__title">Create &amp; test agent</h1>
        <p class="wizard__subtitle">Fill in the steps to build, test, and publish the agent.</p>
      </div>
      <div class="wizard__topbar-actions">
        <button type="button" class="pill-btn" (click)="cancel()">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('back')"></path></svg>
          Back to agents
        </button>
      </div>
    </header>

    <div class="studio-grid">
      <section class="card wizard__card">
        <div class="wizard__card-header">
          <span class="badge-pill">Create &amp; test</span>
          <h2 class="wizard__card-title">Create agent</h2>
          <p class="wizard__card-subtitle">Step {{ step }} of {{ stepTitles.length }}: {{ stepTitles[step - 1] }}. Build, test, and publish the agent from this single screen.</p>
        </div>

        <div class="step-tabs" role="tablist" aria-label="Agent setup steps">
          @for (title of stepTitles; track title; let i = $index) {
            <button type="button" class="step-tab" role="tab" [attr.aria-selected]="step === i + 1"
                    [class.step-tab--active]="step === i + 1" [class.step-tab--complete]="stepHasContent(i + 1)"
                    (click)="selectStep(i + 1)">
              <span class="step-tab__index">
                @if (stepHasContent(i + 1) && step !== i + 1) {
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('check')"></path></svg>
                } @else {
                  {{ i + 1 }}
                }
              </span>
              <span>{{ title }}</span>
            </button>
          }
        </div>

        <form (ngSubmit)="step === stepTitles.length ? finish() : next()">
          <div class="wizard-grid">
            @if (error) {
              <p class="error-banner wizard-grid__full">{{ error }}</p>
            }

            @if (stepKey === 'identity') {
              <div class="of-field wizard-grid__full">
                <kx-multiselect-dropdown [questions]="domainQuestion" [settings]="selectSettings" [data]="domainOptions"
                  [placeholder]="'Choose a domain'" [(ngModel)]="domain" name="domain" [disabled]="copyMode"></kx-multiselect-dropdown>
                <label>Domain*</label>
              </div>
              <div class="of-field wizard-grid__full">
                <input type="text" class="of-control" placeholder="claims-assistant" [(ngModel)]="agentName" name="agentName" [disabled]="copyMode">
                <label>Agent name*</label>
              </div>
              <div class="of-field wizard-grid__full">
                <input type="text" class="of-control" placeholder="Claims Assistant" [(ngModel)]="displayName" name="displayName">
                <label>Display name*</label>
              </div>
            }

            @if (stepKey === 'instructions') {
              <div class="of-field of-field--area wizard-grid__full">
                <textarea class="of-control" rows="3" [(ngModel)]="goal" name="goal"
                          placeholder="Review incoming customer requests, identify the issue category, and route them to the correct team."></textarea>
                <label>What should this agent do?*</label>
              </div>
              <div class="of-field of-field--area wizard-grid__full">
                <textarea class="of-control" rows="5" [(ngModel)]="instructions" name="instructions"
                          placeholder="When a request arrives: 1) identify the issue type, 2) classify severity, 3) choose the correct team, 4) return a concise recommendation."></textarea>
                <label>Instructions (system prompt)</label>
                <span class="of-hint">Falls back to the goal above when left blank.</span>
              </div>
            }

            @if (stepKey === 'model') {
              <div class="of-field">
                <kx-multiselect-dropdown [questions]="providerQuestion" [settings]="selectSettings" [data]="providerOptions"
                  [placeholder]="providers.length ? 'Select a provider' : 'Loading providers…'" [ngModel]="providerName" name="providerName"
                  (onSelect)="$event?.isManual && selectProvider($event.id)" (onDeSelect)="selectProvider('')"></kx-multiselect-dropdown>
                <label>Provider*</label>
              </div>
              <div class="of-field">
                <kx-multiselect-dropdown [questions]="modelQuestion" [settings]="selectSettings" [data]="modelOptions"
                  [placeholder]="'Select a model'" [ngModel]="modelId" name="modelId"
                  (onSelect)="$event?.isManual && selectModel($event.id)" (onDeSelect)="selectModel('')"></kx-multiselect-dropdown>
                <label>Model*</label>
                <span class="of-hint">From the AI model master, the same list AI Prompt Master uses.</span>
              </div>
              <div class="of-field">
                <input type="password" class="of-control" autocomplete="new-password" [(ngModel)]="apiKey" name="apiKey"
                       [placeholder]="apiKeySaved ? 'Saved (hidden)' : 'Enter API key'">
                <label>API key{{ apiKeySaved ? '' : '*' }}</label>
                @if (apiKeyHint) {
                  <span class="of-hint">{{ apiKeyHint }}</span>
                }
              </div>
              <div class="of-field">
                <input type="number" class="of-control" min="0" max="1" step="0.1" [(ngModel)]="temperature" name="temperature">
                <label>Temperature</label>
              </div>
            }

            @if (stepKey === 'tools') {
              <p class="field-note wizard-grid__full">Tools the agent can call. Each one becomes an HTTP request the model can invoke when it needs live data.</p>
              <div class="of-field">
                <kx-multiselect-dropdown [questions]="toolQuestion" [settings]="toolSettings" [data]="toolSource"
                  [placeholder]="'Search the tool registry'" [ngModel]="registryToolPick" name="registryToolPick"
                  (onSelect)="$event?.isManual && pickRegistryTool($event)" (onDeSelect)="pickRegistryTool(null)"></kx-multiselect-dropdown>
                <label>From the tool registry</label>
              </div>
              <div class="inline-action">
                <button type="button" class="outline-btn" [disabled]="!registryToolPick" (click)="addToolFromRegistry()">Add from registry</button>
              </div>
              @for (tool of tools; track $index; let i = $index) {
                <div class="of-field">
                  <input type="text" class="of-control" placeholder="Invoice lookup" [(ngModel)]="tool.name" [name]="'toolName' + i" (ngModelChange)="onToolNameChange(tool)">
                  <label>Name</label>
                </div>
                <div class="of-field">
                  <kx-multiselect-dropdown [questions]="methodQuestion" [settings]="selectSettings" [data]="methodOptions"
                    [placeholder]="'Choose a method'" [(ngModel)]="tool.method" [name]="'toolMethod' + i"></kx-multiselect-dropdown>
                  <label>Method</label>
                </div>
                <div class="of-field wizard-grid__full">
                  <input type="text" class="of-control" placeholder="https://api.example.com/lookup" [(ngModel)]="tool.url" [name]="'toolUrl' + i">
                  <label>URL</label>
                </div>
                <div class="of-field wizard-grid__full">
                  <input type="text" class="of-control" placeholder="Look up an invoice by id" [(ngModel)]="tool.description" [name]="'toolDescription' + i">
                  <label>Description (tells the model when to use it)</label>
                </div>
                <div class="tool-remove wizard-grid__full">
                  <button type="button" class="text-btn text-btn--danger" (click)="removeTool(i)">Remove tool</button>
                </div>
              }
              <div class="wizard-grid__full">
                <button type="button" class="outline-btn" (click)="addTool()">+ Add tool</button>
                <p class="field-note field-note--small">HTTP tools must be allowed for agent drafts on the workflow engine; saving tells you if they are not.</p>
              </div>
            }

            @if (stepKey === 'knowledge') {
              <p class="field-note wizard-grid__full">Knowledge bases the agent can search. Each one becomes a search tool the model picks by its description, and answers cite what they found.</p>
              <div class="of-field">
                <kx-multiselect-dropdown [questions]="knowledgeQuestion" [settings]="toolSettings" [data]="knowledgeSource"
                  [placeholder]="'Search knowledge bases'" [ngModel]="knowledgePick" name="knowledgePick"
                  (onSelect)="$event?.isManual && pickKnowledgeBase($event)" (onDeSelect)="pickKnowledgeBase(null)"></kx-multiselect-dropdown>
                <label>Knowledge base</label>
              </div>
              <div class="inline-action">
                <button type="button" class="outline-btn" [disabled]="!knowledgePick" (click)="addKnowledgeBase()">Add knowledge base</button>
              </div>

              @if (!newSource) {
                <div class="kb-new-actions wizard-grid__full">
                  <span class="field-note">Or add a new source:</span>
                  <button type="button" class="outline-btn" (click)="openNewSource('website')">+ Website</button>
                  <button type="button" class="outline-btn" (click)="openNewSource('mongodb')">+ MongoDB collection</button>
                </div>
              } @else {
                <div class="kb-new wizard-grid__full">
                  <h3 class="kb-new__title">{{ newSource === 'website' ? 'New website knowledge base' : 'New MongoDB knowledge base' }}</h3>
                  <div class="wizard-grid">
                    @if (knowledgeError) {
                      <p class="error-banner wizard-grid__full">{{ knowledgeError }}</p>
                    }
                    @if (newSource === 'website') {
                      <div class="of-field of-field--area wizard-grid__full">
                        <textarea class="of-control" rows="2" placeholder="https://www.example.com/" [(ngModel)]="knowledgeForm.websiteUrls"
                                  name="kbWebsiteUrls" (ngModelChange)="onWebsiteUrlsChange()"></textarea>
                        <label>Start URLs*</label>
                        <span class="of-hint">One per line. Only pages on these sites are crawled, and robots.txt is respected.</span>
                      </div>
                    }
                    <div class="of-field wizard-grid__full">
                      <input type="text" class="of-control" placeholder="Product documentation" [(ngModel)]="knowledgeForm.name"
                             name="kbName" (ngModelChange)="onKnowledgeNameChange()">
                      <label>Name*</label>
                    </div>
                    <div class="of-field of-field--area wizard-grid__full">
                      <textarea class="of-control" rows="2" placeholder="What it holds, e.g. resolved helpdesk tickets with their fixes"
                                [(ngModel)]="knowledgeForm.description" name="kbDescription" (ngModelChange)="onKnowledgeDescriptionChange()"></textarea>
                      <label>Description*</label>
                      <span class="of-hint">The agent picks which knowledge base to search by this.</span>
                    </div>
                    @if (newSource === 'website') {
                      <details class="kb-advanced wizard-grid__full">
                        <summary>Crawl settings</summary>
                        <div class="wizard-grid">
                          <div class="of-field">
                            <input type="number" class="of-control" min="0" max="3" [(ngModel)]="knowledgeForm.maxDepth" name="kbMaxDepth">
                            <label>Link depth</label>
                            <span class="of-hint">How many links away from the start pages to follow, 0 to 3.</span>
                          </div>
                          <div class="of-field">
                            <input type="number" class="of-control" min="1" max="500" [(ngModel)]="knowledgeForm.maxPages" name="kbMaxPages">
                            <label>Max pages</label>
                            <span class="of-hint">Up to 500. More pages take longer to build.</span>
                          </div>
                          <div class="of-field">
                            <input type="text" class="of-control" placeholder="*/docs/*" [(ngModel)]="knowledgeForm.includePatterns" name="kbIncludePatterns">
                            <label>Only URLs matching</label>
                          </div>
                          <div class="of-field">
                            <input type="text" class="of-control" placeholder="*/blog/*, */tag/*" [(ngModel)]="knowledgeForm.excludePatterns" name="kbExcludePatterns">
                            <label>Skip URLs matching</label>
                          </div>
                          <div class="of-field">
                            <input type="number" class="of-control" min="0" [(ngModel)]="knowledgeForm.refreshHours" name="kbRefreshHours">
                            <label>Re-crawl every (hours)</label>
                            <span class="of-hint">0 re-crawls only when the knowledge base is saved again.</span>
                          </div>
                        </div>
                      </details>
                    } @else {
                      <div class="of-field wizard-grid__full">
                        <input type="password" class="of-control" autocomplete="new-password" placeholder="mongodb+srv://user:pass@cluster.mongodb.net"
                               [(ngModel)]="knowledgeForm.connectionString" name="kbConnectionString">
                        <label>Connection string*</label>
                        <span class="of-hint">Stored encrypted and never shown again.</span>
                      </div>
                      <div class="of-field">
                        <input type="text" class="of-control" placeholder="helpdesk" [(ngModel)]="knowledgeForm.dbName" name="kbDbName">
                        <label>Database*</label>
                      </div>
                      <div class="of-field">
                        <input type="text" class="of-control" placeholder="tickets" [(ngModel)]="knowledgeForm.collectionName" name="kbCollectionName">
                        <label>Collection*</label>
                      </div>
                      <div class="of-field">
                        <input type="text" class="of-control" placeholder="subject,description,resolution" [(ngModel)]="knowledgeForm.fields" name="kbFields">
                        <label>Fields to embed*</label>
                        <span class="of-hint">Comma-separated source fields; a dotted path reads inside records and lists, e.g. module_list.module_name.</span>
                      </div>
                      <div class="of-field">
                        <input type="text" class="of-control" placeholder="subject" [(ngModel)]="knowledgeForm.titleField" name="kbTitleField">
                        <label>Title field</label>
                        <span class="of-hint">Titles each document; leave empty to pick one by name.</span>
                      </div>
                      <div class="of-field of-field--area wizard-grid__full">
                        <textarea class="of-control" rows="2" placeholder='{"status": "closed"}' [(ngModel)]="knowledgeForm.filter" name="kbFilter"></textarea>
                        <label>Filter (JSON)</label>
                        <span class="of-hint">Leave empty to embed every document.</span>
                      </div>
                    }
                  </div>
                  <div class="kb-new__actions">
                    <span class="field-note field-note--small">It starts building as soon as it is created.</span>
                    <button type="button" class="text-btn" (click)="closeNewSource()">Cancel</button>
                    <button type="button" class="primary-btn" [disabled]="creatingKnowledge" (click)="createKnowledgeBase()">
                      {{ creatingKnowledge ? 'Creating…' : 'Create and add' }}
                    </button>
                  </div>
                </div>
              }

              @for (kb of knowledgeBases; track kb.id; let i = $index) {
                <div class="kb-card wizard-grid__full" [attr.data-state]="knowledgeState(kb)">
                  <div class="kb-card__head">
                    <div class="kb-card__name">
                      <strong>{{ kb.name }}</strong>
                      @if (kb.sourceType) {
                        <span class="kb-card__source">{{ kb.sourceType === 'website' ? 'Website' : 'MongoDB' }}</span>
                      }
                    </div>
                    <span class="kb-chip">{{ knowledgeStateLabel(kb) }}</span>
                  </div>
                  @if (kb.description) {
                    <p class="kb-card__text">{{ kb.description }}</p>
                  }
                  @if (isBuilding(kb)) {
                    <div class="kb-progress" role="progressbar" aria-label="Building"><span></span></div>
                  }
                  @if (knowledgeProgress(kb)) {
                    <p class="kb-card__progress" aria-live="polite">{{ knowledgeProgress(kb) }}</p>
                  }
                  @if (kb.errorMessage && !isBuilding(kb)) {
                    <p class="kb-card__message">{{ kb.errorMessage }}</p>
                  }
                  <div class="kb-card__actions">
                    @if (kb.status === 'failed') {
                      <button type="button" class="text-btn" (click)="retryKnowledgeBase(kb)">Retry build</button>
                    }
                    <button type="button" class="text-btn text-btn--danger" (click)="removeKnowledgeBase(i)">Remove from agent</button>
                  </div>
                </div>
              }
              @if (knowledgeBases.length) {
                <div class="form-check form-switch toggle-row wizard-grid__full">
                  <input class="form-check-input" type="checkbox" role="switch" id="knowledgeOnly" [(ngModel)]="knowledgeOnly" name="knowledgeOnly">
                  <label class="form-check-label" for="knowledgeOnly">Answer only from these knowledge bases</label>
                </div>
              }
              <p class="field-note field-note--small wizard-grid__full">Builds run on the server: keep going through the steps, or leave this page, and Knowledge Bases shows the same progress. Publishing waits until every knowledge base is Ready, and an account copy can use only global knowledge bases and its own account's.</p>
              @if (copyMode && knowledgeNotReady.length) {
                <p class="field-note field-note--small wizard-grid__full">An account copy runs as soon as it is saved, so Next saves it only once every knowledge base is Ready.</p>
              }
            }

            @if (triggersVisited) {
              <div class="wizard-grid__full" [hidden]="stepKey !== 'triggers'">
                <app-agent-trigger-editor [setupId]="draftId" [readOnly]="copyMode"></app-agent-trigger-editor>
              </div>
            }

            @if (stepKey === 'guardrails') {
              <div class="form-check form-switch toggle-row wizard-grid__full">
                <input class="form-check-input" type="checkbox" role="switch" id="guardrailsEnabled" [(ngModel)]="guardrailsEnabled" name="guardrailsEnabled">
                <label class="form-check-label" for="guardrailsEnabled">Enable guardrails</label>
              </div>
              <p class="field-note wizard-grid__full">Checks every message before the agent sees it and refuses prompt-injection attempts and empty or oversized input.</p>
              @if (guardrailsEnabled) {
                <div class="of-field wizard-grid__full">
                  <input type="text" class="of-control" placeholder="default" [(ngModel)]="guardrailsConfigId" name="guardrailsConfigId">
                  <label>Guardrails config ID</label>
                </div>
              }
            }

            @if (stepKey === 'memory') {
              <div class="form-check form-switch toggle-row wizard-grid__full">
                <input class="form-check-input" type="checkbox" role="switch" id="memoryEnabled" [(ngModel)]="memoryEnabled" name="memoryEnabled">
                <label class="form-check-label" for="memoryEnabled">Remember earlier turns in this conversation</label>
              </div>
              @if (memoryEnabled) {
                <div class="of-field">
                  <input type="text" class="of-control" placeholder="conversation" [(ngModel)]="memoryKey" name="memoryKey">
                  <label>Memory key</label>
                  <span class="of-hint">Leave blank to use "conversation". Memory is always kept per user and session.</span>
                </div>
                <div class="of-field">
                  <input type="number" class="of-control" min="1" [(ngModel)]="memoryMaxEntries" name="memoryMaxEntries">
                  <label>Max remembered turns</label>
                </div>
                <p class="field-note wizard-grid__full">Remembers up to this many prior exchanges per session, so the agent has conversational context on follow-up messages.</p>
              }
            }

            @if (stepKey === 'test' && copyMode) {
              <p class="field-note wizard-grid__full">This is an account's copy of a published version. Run it from the Account agents list after saving.</p>
            }
            @if (stepKey === 'test' && !copyMode) {
              <div class="of-field of-field--area wizard-grid__full">
                <textarea class="of-control" rows="2" [(ngModel)]="testMessage" name="testMessage"
                          placeholder="Ask the agent something to try it before publishing."></textarea>
                <label>Test message</label>
              </div>
              @if (knowledgeNotReady.length) {
                <p class="field-note field-note--small wizard-grid__full">Not ready yet: {{ knowledgeNotReadyNames }}. The test can search a knowledge base only once it has been built.</p>
              }
              @if (triggerEditor && triggerEditor.triggers.length) {
                <div class="trigger-samples wizard-grid__full">
                  <span class="field-note">Or use a trigger's latest event:</span>
                  @for (t of triggerEditor.triggers; track $index; let i = $index) {
                    <button type="button" class="outline-btn" [disabled]="sampling" (click)="useTriggerSample(t)">{{ t.name || 'Trigger ' + (i + 1) }}</button>
                  }
                </div>
              }
              <div class="test-row wizard-grid__full">
                <span class="field-note">Test the agent before publishing it to the domain.</span>
                <button type="button" class="primary-btn" [disabled]="testing || !testMessage.trim()" (click)="runTest()">
                  {{ testing ? 'Running…' : 'Test agent' }}
                </button>
              </div>
              @if (testResult) {
                <div class="test-result wizard-grid__full"><strong>Response:</strong> {{ testResult }}</div>
              }
            }

            @if (stepKey === 'publish' && knowledgeNotReady.length) {
              <p class="error-banner wizard-grid__full">Waiting for knowledge bases to finish building: {{ knowledgeNotReadyNames }}. {{ copyMode ? 'Saving' : 'Publishing' }} is possible once every one is Ready; a failed one can be retried or removed in the Knowledge step.</p>
            }
            @if (stepKey === 'publish' && copyMode) {
              <p class="field-note wizard-grid__full"><strong>Account copy</strong> of {{ domain }} version <strong>{{ version }}</strong>. Saving changes only this copy; the master and other accounts are not affected.</p>
            }
            @if (stepKey === 'publish' && !copyMode) {
              <p class="field-note wizard-grid__full"><strong>Ready to publish:</strong> {{ displayName }} in {{ domain }}.</p>
              @if (triggerCount > 0) {
                <p class="field-note wizard-grid__full"><strong>Triggers:</strong> {{ triggerCount }} trigger{{ triggerCount === 1 ? '' : 's' }}. Draft triggers go live when this version is published (unless "Enable when the agent is published" is off); each account runs its own active copy of the agent.</p>
              }
              @if (previouslyPublishedVersion) {
                <p class="field-note wizard-grid__full">This draft was already published as <strong>{{ previouslyPublishedVersion }}</strong>. Versions are immutable, so enter a new version below to publish an updated one.</p>
              }
              <div class="of-field">
                <input type="text" class="of-control" placeholder="v2" [(ngModel)]="version" name="version">
                <label>Version*</label>
              </div>
              <div class="of-field">
                <kx-multiselect-dropdown [questions]="lifecycleQuestion" [settings]="selectSettings" [data]="lifecycleOptions"
                  [placeholder]="'Choose a lifecycle'" [(ngModel)]="lifecycle" name="lifecycle"></kx-multiselect-dropdown>
                <label>Lifecycle*</label>
              </div>
            }
          </div>

          <div class="wizard__actions">
            <button type="button" class="text-btn" [disabled]="step === 1" (click)="back()">Back</button>
            <button type="button" class="text-btn text-btn--accent" (click)="cancel()">Cancel</button>
            @if (step < stepTitles.length) {
              <button type="submit" class="primary-btn" [disabled]="saving">
                {{ saving ? 'Saving…' : 'Next' }}
              </button>
            } @else {
              @if (copyMode) {
                <button type="button" class="primary-btn" [disabled]="!validFinalStep || saving || knowledgeNotReady.length > 0" (click)="saveCopy()">
                  {{ saving ? 'Saving…' : 'Save copy' }}
                </button>
              } @else {
                <button type="button" class="primary-btn" [disabled]="!validFinalStep || publishing || saving || knowledgeNotReady.length > 0" (click)="publish()">
                  {{ publishing ? 'Publishing…' : 'Create & publish agent' }}
                </button>
              }
            }
          </div>
        </form>
      </section>

    </div>
  </div>

</div>
